<script setup lang="ts">
import LawyerCategories from '@/entities/lawyer/ui/LawyerCategories/index.vue';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import type { ILawyerCategory } from '@/entities/lawyer/model/types/lawyerCategories';

const specializationIcons = import.meta.glob<{ default: Component }>(
  '@/shared/ui/Icons/specializations/*.vue',
);

const toPascalCase = (slug: string): string =>
  slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const getIcon = (slug: string): Component | null => {
  const name = toPascalCase(slug);
  const path = Object.keys(specializationIcons).find((key) =>
    key.endsWith(`/${name}.vue`),
  );
  const loader = path ? specializationIcons[path] : undefined;

  return loader ? defineAsyncComponent(loader) : null;
};

interface SpecializationItem {
  id: number;
  name: string;
  slug: string;
  icon: Component | null;
  subcategories: ILawyerCategory[];
}

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);

const categories: ComputedRef<SpecializationItem[]> = computed(() => {
  const main = lawyerInfo?.value?.mainCategory ?? [];
  const activity = lawyerInfo?.value?.activityCategories ?? [];

  return main.map((item) => ({
    id: item.id,
    name: item.name,
    slug: item.slug,
    icon: getIcon(item.slug),
    subcategories: activity.filter((c) => c.parent_id === item.id),
  }));
});
</script>

<template>
  <article
    v-if="categories.length"
    :class="['specialization-list', { _mobile: isMobile }]"
  >
    <h2 class="specialization-list__title">Специализации</h2>
    <ul class="specialization-list__list">
      <li
        v-for="category in categories"
        :key="category.id"
        class="specialization-list__item"
        data-testid="specialization-item"
      >
        <component
          :is="category.icon"
          v-if="category.icon"
          class="specialization-list__item-icon"
          data-testid="specialization-icon"
        />
        <div class="specialization-list__item-content">
          <div class="specialization-list__item-title">{{ category.name }}</div>
          <LawyerCategories
            :categories="category.subcategories"
            :category-slug="category.slug"
          />
        </div>
      </li>
    </ul>
  </article>
</template>

<style lang="less">
.specialization-list {
  &__title {
    font-weight: 600;
    font-size: @size-x6;
    line-height: 25px;
    margin-bottom: @size-x5;
    margin-top: 0;
  }

  &__item {
    position: relative;
    display: flex;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }

  &__item-title {
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    margin-bottom: @size-x2;
  }

  &__item-icon {
    flex-shrink: 0;
    width: @size-x4;
    height: @size-x4;
  }

  &__item-content {
    margin-left: 5px;
  }

  &._mobile &__title {
    font-size: @size-x4;
    line-height: 22px;
    margin-bottom: @size-x4;
  }
}
</style>
